import { useCallback, useEffect, useRef, useState } from 'react'

import { type InlineEditTextFieldStatus } from '@procurify/ui'

import { toast } from '@webapp/components/Notification'
import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { useMutateRenamePOCustomField } from '../../models'

const RESOLVED_DISPLAY_DURATION_MS = 2000
const normalizeFieldName = (name: string) => name.trim().toLowerCase()

export interface UseEditablePOCustomFieldTextFieldPresenterParams {
  field: CustomField
  allFields: CustomField[]
  onSuccess?: () => void
}

export const useEditablePOCustomFieldTextFieldPresenter = ({
  field,
  allFields,
  onSuccess,
}: UseEditablePOCustomFieldTextFieldPresenterParams) => {
  const intl = useProcurifyIntl()
  const [inputValue, setInputValue] = useState(field.name)
  const [status, setStatus] = useState<InlineEditTextFieldStatus>('idle')
  const isMounted = useRef(true)
  const isCommittingRef = useRef(false)
  const resolvedStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const { mutateAsync } = useMutateRenamePOCustomField()

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      if (resolvedStatusTimeoutRef.current) {
        clearTimeout(resolvedStatusTimeoutRef.current)
        resolvedStatusTimeoutRef.current = null
      }
    }
  }, [])

  // Keep inputValue in sync if the field prop changes externally (refetch)
  useEffect(() => {
    setInputValue(field.name)
  }, [field.name])

  const isDuplicateName = useCallback(
    (name: string): boolean => {
      const normalizedName = normalizeFieldName(name)
      return allFields.some(
        (f) =>
          f.id !== field.id && normalizeFieldName(f.name) === normalizedName
      )
    },
    [allFields, field.id]
  )

  const handleCommit = useCallback(async () => {
    if (isCommittingRef.current) return
    isCommittingRef.current = true

    try {
      const trimmedInputValue = inputValue.trim()

      // No-op if value unchanged
      if (trimmedInputValue === field.name) {
        return
      }

      if (trimmedInputValue === '') {
        toast.warning({
          title: intl.formatMessage(messages.warningTitle),
          children: intl.formatMessage(messages.emptyNameWarning),
        })
        setInputValue(field.name)
        return
      }

      // Client-side duplicate check — no request (BHV-014)
      if (isDuplicateName(trimmedInputValue)) {
        toast.warning({
          title: intl.formatMessage(messages.warningTitle),
          children: intl.formatMessage(messages.duplicateNameWarning),
        })
        setInputValue(field.name)
        return
      }

      setStatus('pending')

      try {
        await mutateAsync({ field, name: trimmedInputValue })

        if (!isMounted.current) return

        setStatus('resolved')
        onSuccess?.()

        resolvedStatusTimeoutRef.current = setTimeout(() => {
          if (!isMounted.current) return
          setStatus('idle')
          resolvedStatusTimeoutRef.current = null
        }, RESOLVED_DISPLAY_DURATION_MS)
      } catch (err) {
        if (!isMounted.current) return

        setStatus('rejected')
        setInputValue(field.name)

        const errorMessage = getMessageFromError(err)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage || intl.formatMessage(messages.errorFallback),
        })
      }
    } finally {
      isCommittingRef.current = false
    }
  }, [inputValue, field, isDuplicateName, mutateAsync, intl, onSuccess])

  const handleEscape = useCallback(() => {
    setInputValue(field.name)
    setStatus('idle')
  }, [field.name])

  const handleChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  return {
    data: {
      inputValue,
      status,
    },
    handlers: {
      handleChange,
      handleCommit,
      handleEscape,
    },
  }
}
